import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reportsApi } from '../services/api';
import { useAuth } from '../contexts/SupabaseAuthContext';
import { toast } from 'sonner';

export function useReports() {
  const queryClient = useQueryClient();
  const { user, loading: authLoading } = useAuth();

  // Get all reports
  const useAllReports = (projectId?: string) => {
    return useQuery({
      queryKey: ['reports', { projectId }],
      queryFn: () => reportsApi.getReports(projectId),
      enabled: !authLoading && !!user,
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  };

  // Get single report
  const useReport = (reportId: string) => {
    return useQuery({
      queryKey: ['reports', reportId],
      queryFn: () => reportsApi.getReport(reportId),
      enabled: !authLoading && !!user && !!reportId,
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  };

  // Get report templates
  const useTemplates = () => {
    return useQuery({
      queryKey: ['reports', 'templates'],
      queryFn: () => reportsApi.getTemplates(),
      enabled: !authLoading && !!user,
      staleTime: 30 * 60 * 1000, // 30 minutes - templates don't change often
    });
  };

  // Get scheduled reports
  const useScheduledReports = (projectId?: string) => {
    return useQuery({
      queryKey: ['reports', 'scheduled', { projectId }],
      queryFn: () => reportsApi.getScheduledReports(projectId),
      enabled: !authLoading && !!user,
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  };

  // Create report mutation
  const createReportMutation = useMutation({
    mutationFn: reportsApi.createReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success('Report created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create report');
    },
  });

  // Update report mutation
  const updateReportMutation = useMutation({
    mutationFn: ({ reportId, data }: { reportId: string; data: any }) => 
      reportsApi.updateReport(reportId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      queryClient.invalidateQueries({ queryKey: ['reports', variables.reportId] });
      toast.success('Report updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update report');
    },
  });

  // Delete report mutation
  const deleteReportMutation = useMutation({
    mutationFn: reportsApi.deleteReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      queryClient.invalidateQueries({ queryKey: ['reports', 'scheduled'] });
      toast.success('Report deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete report');
    },
  });

  // Generate report mutation
  const generateReportMutation = useMutation({
    mutationFn: reportsApi.generateReport,
    onSuccess: (_, reportId) => {
      // Invalidate both the individual report and the reports list
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      queryClient.invalidateQueries({ queryKey: ['reports', reportId] });
      toast.success('Report generated successfully');
    },
    onError: (error: any) => {
      // Also invalidate on error to refresh status
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.error(error.response?.data?.message || 'Failed to generate report');
    },
  });

  // Export report
  const exportReport = async (reportId: string, format: string) => {
    try {
      const blob = await reportsApi.exportReport(reportId, format);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-${reportId}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      return true;
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
      return false;
    }
  };

  return {
    useAllReports,
    useReport,
    useTemplates,
    useScheduledReports,
    createReport: createReportMutation.mutateAsync,
    updateReport: updateReportMutation.mutateAsync,
    deleteReport: deleteReportMutation.mutateAsync,
    generateReport: generateReportMutation.mutateAsync,
    exportReport,
    isCreating: createReportMutation.isPending,
    isUpdating: updateReportMutation.isPending,
    isDeleting: deleteReportMutation.isPending,
    isGenerating: generateReportMutation.isPending,
  };
}